import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Range as LsTypesRange } from 'vscode-languageserver-types';
import BottomActionButtons from '../Components/AppBars/BottomBar/BottomActionsButtons';
import BottomControlBar from '../Components/AppBars/BottomBar/BottomControlBar';
import DeployButton from '../Components/AppBars/BottomBar/DeployButton';
import BottomDrawer from '../Components/Drawers/BottomDrawer/BottomDrawer';
import { MonacoEditor } from '../Components/Editor/Monaco/MonacoEditor';
import { projectSlice } from '../redux/ProjectSlice';
import { useAppDispatch, useAppSelector } from '../redux/ReduxHooks';
import dragImage from '../Components/CodeEditor/assets/drag-image.svg';
import CodeEditorLayout from '../Components/CodeEditor/CodeEditorLayout';
import ActionArgumentsDialog from '../Components/CodeEditor/components/ActionArgumentsDialog';
import DeviceSnippetPortDialog from '../Components/CodeEditor/components/DeviceSnippetPortDialog';
import NoPathsDialog from '../Components/CodeEditor/components/NoPathsDialog';
import PathSelectionDialog from '../Components/CodeEditor/components/PathSelectionDialog';
import { useEditorDialogs } from '../hooks/editor/useEditorDialogs';
import { useEditorDragDrop } from '../hooks/editor/useEditorDragDrop';
import { useMonacoEditor } from '../hooks/editor/useMonacoEditor';
import { useLeftDrawerLayout } from '../hooks/ui/useLeftDrawerLayout';
import { useLogging } from '../logging/logging';
import { equals } from 'ramda';
import {
  setActionList,
  setAxisActionList,
  setCustomActionList,
  setFrameworkActionLists,
  setInputBindingList,
  setStartCallConfig,
} from '../redux/EditorSlice';
import { useClearEditorStateWhenCodeEmpty } from '../Components/CodeEditor/ConfigurationTabs/useClearEditorStateWhenCodeEmpty';
import type { ICardComponent } from '../Components/CodeEditor/Devices/DeviceCardBuilder';
import { parseHardwareFromProjectCode } from '../Components/CodeEditor/ConfigurationTabs/hardwareParser';
import { applicationSlice } from '../redux/ApplicationSlice';
import { buildDriveUnitPortOccupanciesFromHardwareCards } from '../utils/buildDriveUnitPortOccupanciesFromHardwareCards';
import { formatActivePythonProgram } from '../utils/pythonFormattingUtils';
import { useDriveUnitPortStreamSyncFromEditor } from '../hooks/controls/useDriveUnitPortStreamSyncFromEditor';
import {
  isPythonTreeSitterReady,
  notifyPythonTreeSitterBindingsHydrateFailed,
  notifyPythonTreeSitterBindingsHydrated,
  prewarmPythonTreeSitter,
  requestPythonTreeSitterBindingsHydrate,
  subscribePythonTreeSitterReadyTransition,
} from '../utils/pythonTreeSitterInputBindings';
import { useSnippetAnnouncements } from '../hooks/editor/useSnippetAnnouncements';
import { useMeasuredBottomSnackbarStackIndex } from '../Components/Snackbars/SnackbarStackContext';
import { bottomCenterSnackbarStackSx, defaultSnackbarContentSx } from '../Components/Snackbars/snackbarStyles';

const EMPTY_INPUT_BINDINGS: [] = [];
const POINTER_DRAG_ACTIVATION_DISTANCE = 5;
const SILENT_DND_ACCESSIBILITY = {
  announcements: {
    onDragStart: () => undefined,
    onDragOver: () => undefined,
    onDragEnd: () => undefined,
    onDragCancel: () => undefined,
  },
  screenReaderInstructions: {
    draggable: '',
  },
  restoreFocus: false,
};

const CodeEditorPage = () => {
  const { t } = useTranslation();
  const { leftDrawer, leftDrawerContentExpanded, leftDrawerWidth, leftDrawerContentWidth, toggleLeftDrawer } = useLeftDrawerLayout('codeEditor');
  const dispatch = useAppDispatch();
  const logger = useLogging();

  const readOnly = useAppSelector((state) => state.project.loadedProject?.readOnly) ?? false;
  const projectId = useAppSelector((state) => state.project.loadedProject?.id);
  const dashboardLayout = useAppSelector((state) => state.project.loadedProject?.dashboardLayout);
  const projectCode = useAppSelector((state) => state.project.loadedProject?.program?.code ?? '');
  const existingInputBindings = useAppSelector((state) => state.editor.inputBindingList) ?? EMPTY_INPUT_BINDINGS;

  const [hardwareDynamicCards, setHardwareDynamicCards] = useState<ICardComponent[]>([]);
  const [hardwareIsLoading, setHardwareIsLoading] = useState(false);
  const [hardwareHasParsed, setHardwareHasParsed] = useState(false);
  const [pythonIsFormatting, setPythonIsFormatting] = useState(false);
  const [driveUnitPortRegistryRev, setDriveUnitPortRegistryRev] = useState(0);
  const {
    status: snippetStatus,
    announceInserted: handleSnippetInserted,
    announceRejected: handleSnippetRejected,
    announcePlacementRequested: handleSnippetPlacementRequested,
    announcePlacementInvalid: handleSnippetPlacementInvalid,
    announcePlacementCancelled: handleSnippetPlacementCancelled,
    formatPlacementInstructions,
  } = useSnippetAnnouncements();
  const dndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: POINTER_DRAG_ACTIVATION_DISTANCE },
    })
  );
  const existingInputBindingsRef = useRef(existingInputBindings);
  existingInputBindingsRef.current = existingInputBindings;
  const lspParseRunIdRef = useRef(0);
  const hardwareCardsRef = useRef<ICardComponent[]>([]);
  // Bumped when Tree-sitter transitions to ready so hardware parse re-runs after a late init.
  const [treeSitterReadyEpoch, setTreeSitterReadyEpoch] = useState(0);

  // Warm Tree-sitter as soon as the editor opens so Control mutations are less likely
  // to race the debounced hardware parse. Init is the readiness probe: prewarm retries
  // with backoff on failure; each hardware parse also re-attempts init. On a not-ready →
  // ready transition (including Control Retry), request a bindings hydrate so mutations
  // stay gated until this page's parse commits.
  useEffect(() => {
    return subscribePythonTreeSitterReadyTransition(() => {
      setTreeSitterReadyEpoch((epoch) => epoch + 1);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const wasReady = isPythonTreeSitterReady();
    void prewarmPythonTreeSitter().then((ok) => {
      if (cancelled) return;
      if (!ok) {
        logger.error('Tree-sitter WASM prewarm failed');
        return;
      }
      if (!wasReady) {
        requestPythonTreeSitterBindingsHydrate();
      }
    });
    return () => {
      cancelled = true;
    };
  }, [logger]);

  const handleLeftDrawer = useCallback(() => {
    // Prefer centralized hook toggle for consistency
    if (toggleLeftDrawer) {
      toggleLeftDrawer();
    } else {
      dispatch(projectSlice.actions.toggleEditorOpenDrawer());
    }
  }, [dispatch, toggleLeftDrawer]);

  const {
    editorState,
    editorRef,
    standaloneEditorRef,
    editorInstance,
    previewModelRef,
    handleEditorMounted,
    onUpdateCode,
    debouncedSync,
    lspClient,
  } = useMonacoEditor();

  useEffect(() => {
    hardwareCardsRef.current = hardwareDynamicCards;
  }, [hardwareDynamicCards]);

  useClearEditorStateWhenCodeEmpty(projectCode, dispatch, () => {
    setHardwareDynamicCards([]);
    setHardwareIsLoading(false);
    setHardwareHasParsed(false);
    hardwareCardsRef.current = [];
    setDriveUnitPortRegistryRev(0);
    dispatch(applicationSlice.actions.resetDriveUnitPorts());
  });

  useEffect(() => {
    if (projectCode === '') return;
    // Prevent empty-state flash: treat hardware as loading until we complete a parse for this code.
    setHardwareHasParsed(false);
    setHardwareIsLoading(true);
  }, [projectCode]);

  useEffect(() => {
    if (projectCode === '') {
      // No program text to hydrate — do not leave Control mutations stuck after a ready transition.
      notifyPythonTreeSitterBindingsHydrated();
      return;
    }

    // Parse immediately using text scanning; later runs refine results once LSP/editor are ready.
    lspParseRunIdRef.current += 1;
    const runId = lspParseRunIdRef.current;
    let cancelled = false;
    setHardwareIsLoading(true);
    const handle = setTimeout(async () => {
      try {
        const existingForParse = existingInputBindingsRef.current ?? EMPTY_INPUT_BINDINGS;
        const parsed = await parseHardwareFromProjectCode({
          code: projectCode,
          editor: editorInstance ?? undefined,
          lspClient,
          existingInputBindings: existingForParse,
          logger,
        });
        if (cancelled || runId !== lspParseRunIdRef.current) return;

        dispatch(setActionList(parsed.actionList));
        dispatch(setAxisActionList(parsed.axisActionList));
        dispatch(setCustomActionList(parsed.customActionList));
        dispatch(setFrameworkActionLists(parsed.frameworkActionLists));
        dispatch(setStartCallConfig(parsed.startCallConfig));
        if (!equals(parsed.inputBindingList, existingForParse)) {
          dispatch(setInputBindingList(parsed.inputBindingList));
        }
        const prevCards = hardwareCardsRef.current;
        const nextCards = parsed.dynamicCards;
        if (!equals(prevCards, nextCards)) {
          setDriveUnitPortRegistryRev((n) => n + 1);
        }
        dispatch(applicationSlice.actions.clearNonSyntheticDriveUnitPorts());
        // Pin to `projectCode`: these cards were parsed from that snapshot, and reading the live
        // model instead would mix two program texts whenever the user typed during the parse,
        // dropping rows for cards whose declaration has since moved or been renamed.
        const driveUnitRows = buildDriveUnitPortOccupanciesFromHardwareCards(nextCards, editorInstance ?? undefined, projectCode, {
          programTextSource: 'project-code',
        });
        for (const row of driveUnitRows) {
          dispatch(applicationSlice.actions.onUpdateDriveUnitPorts(row));
        }
        setHardwareDynamicCards((prev) => (equals(prev, nextCards) ? prev : nextCards));

        // Unlock Control only after a successful binding hydrate — not when discovery
        // failed and existing Redux bindings were merely preserved.
        if (parsed.inputBindingDiscoverySucceeded) {
          notifyPythonTreeSitterBindingsHydrated();
        } else if (isPythonTreeSitterReady()) {
          notifyPythonTreeSitterBindingsHydrateFailed();
        }
      } catch (err) {
        // Parse threw before returning a result — leave prior Redux/UI state alone and fail-closed
        // Control mutations when Tree-sitter is ready so Retry can re-hydrate.
        logger.error('Error parsing mechanisms', err);
        if (!cancelled && runId === lspParseRunIdRef.current && isPythonTreeSitterReady()) {
          notifyPythonTreeSitterBindingsHydrateFailed();
        }
      } finally {
        if (!cancelled && runId === lspParseRunIdRef.current) {
          setHardwareIsLoading(false);
          setHardwareHasParsed(true);
        }
      }
    }, 50);

    return () => {
      cancelled = true;
      clearTimeout(handle);
      // Invalidate in-flight parse so a completion after unmount/dep-change cannot dispatch.
      lspParseRunIdRef.current += 1;
      setHardwareIsLoading(false);
    };
  }, [projectCode, editorInstance, lspClient, dispatch, logger, treeSitterReadyEpoch]);

  useDriveUnitPortStreamSyncFromEditor({
    editor: editorInstance,
    hardwareCards: hardwareDynamicCards,
    readOnly,
    projectId,
    dashboardLayout,
    projectCode,
  });

  const handleSelectRange = useCallback(
    (range: LsTypesRange) => {
      if (!editorRef.current) return;
      editorRef.current.selectRange(range as unknown as LsTypesRange);
    },
    [editorRef]
  );

  const dialogs = useEditorDialogs({
    standaloneEditorRef,
    debouncedSync,
    onSnippetInserted: handleSnippetInserted,
  });

  const dragDrop = useEditorDragDrop({
    standaloneEditorRef,
    previewModelRef,
    debouncedSync,
    onPathDropRequested: dialogs.requestPathDrop,
    onNoPathsAvailable: dialogs.requestNoPathsDialog,
    onActionArgsDropRequested: dialogs.requestActionArgsDrop,
    onDeviceSnippetPortDropRequested: dialogs.requestDeviceSnippetPortDrop,
    onDuplicateDriveBaseDropRequested: dialogs.requestDuplicateDriveBaseDialog,
    onSnippetInserted: handleSnippetInserted,
    onSnippetRejected: handleSnippetRejected,
    onSnippetPlacementRequested: handleSnippetPlacementRequested,
    onSnippetPlacementInvalid: handleSnippetPlacementInvalid,
    onSnippetPlacementCancelled: handleSnippetPlacementCancelled,
  });

  const invalidDragPlacementOpen = dragDrop.isDragging && Boolean(dragDrop.draggingSnippetPlacementInvalidReason);
  const snippetPlacementOpen = Boolean(dragDrop.pendingSnippetPlacement) || invalidDragPlacementOpen;
  const { ref: snippetPlacementSnackbarRef, stackIndex: snippetPlacementStackIndex } = useMeasuredBottomSnackbarStackIndex<HTMLDivElement>({
    horizontal: 'center',
    open: snippetPlacementOpen,
  });
  const snippetPlacementPayload = dragDrop.pendingSnippetPlacement ?? (invalidDragPlacementOpen ? dragDrop.dropInfo.dropObject : null);
  const snippetPlacementInvalidReason = dragDrop.pendingSnippetPlacement
    ? dragDrop.pendingSnippetPlacementInvalidReason
    : dragDrop.draggingSnippetPlacementInvalidReason;
  const snippetPlacementInstructions = snippetPlacementPayload
    ? formatPlacementInstructions(snippetPlacementPayload.title, snippetPlacementInvalidReason)
    : '';

  const handleFormatPythonProgram = useCallback(async () => {
    const editor = standaloneEditorRef.current;
    if (!editor || !editor.getModel() || readOnly || pythonIsFormatting) return;

    setPythonIsFormatting(true);
    try {
      const acceptedCode = await formatActivePythonProgram(editor);
      if (acceptedCode != null) onUpdateCode(acceptedCode);
    } finally {
      setPythonIsFormatting(false);
    }
  }, [onUpdateCode, pythonIsFormatting, readOnly, standaloneEditorRef]);

  const formatPythonActionLabel = t('bottomControlBar.formatPython', 'Format Python code');

  return (
    <DndContext
      accessibility={SILENT_DND_ACCESSIBILITY}
      sensors={dndSensors}
      onDragCancel={dragDrop.handleDragCancel}
      onDragEnd={dragDrop.handleDrop}
      onDragStart={dragDrop.handleDragStart}
      onDragMove={dragDrop.handleDragging}
      collisionDetection={dragDrop.fixCursorSnapOffset}
    >
      <Box height="100vh" width="100vw" minWidth={0} minHeight={0} sx={{ display: 'flex' }} overflow="hidden">
        <CodeEditorLayout
          readOnly={readOnly}
          leftDrawer={leftDrawer ?? false}
          drawerWidth={leftDrawerContentWidth}
          editorInstance={editorInstance}
          editorState={editorState}
          onLeftDrawerToggle={handleLeftDrawer}
          hardwareDynamicCards={hardwareDynamicCards}
          hardwareIsLoading={hardwareIsLoading || !hardwareHasParsed}
          driveUnitPortRegistryRev={driveUnitPortRegistryRev}
          onInsertSnippet={dragDrop.requestSnippetPlacement}
        >
          <Stack direction="row" py={0} pr={0} pl={0} flexGrow={1} minWidth={0} minHeight={0} alignItems={'flex-start'}>
            <MonacoEditor
              ref={editorRef}
              onEditorMounted={handleEditorMounted}
              dropInfo={dragDrop.dropInfo}
              lspClient={lspClient}
              code={editorState.code}
              previewModelRef={previewModelRef}
              readOnly={readOnly}
              diagnostics={editorState.diagnostics}
              onUpdateCode={onUpdateCode}
              formatPythonActionLabel={formatPythonActionLabel}
              onFormatPythonProgram={handleFormatPythonProgram}
            />
          </Stack>
          <BottomDrawer
            diagnostics={editorState.diagnostics}
            onSelectRange={handleSelectRange}
            displayActivityIndicator={editorState.isWaitingForResponse}
            leftDrawerOpen={leftDrawerContentExpanded}
            leftDrawerWidth={leftDrawerWidth}
          />
        </CodeEditorLayout>
        {/* Bottom Control Bar */}
        <BottomControlBar
          onUndo={() => editorInstance?.trigger('keyboard', 'undo', null)}
          onRedo={() => editorInstance?.trigger('keyboard', 'redo', null)}
          onZoomIn={() => editorInstance?.trigger('keyboard', 'editor.action.fontZoomIn', null)}
          onZoomOut={() => editorInstance?.trigger('keyboard', 'editor.action.fontZoomOut', null)}
          onZoomReset={() => editorInstance?.trigger('keyboard', 'editor.action.fontZoomReset', null)}
          canUndo={true}
          canRedo={true}
          centerContent={<BottomActionButtons />}
          rightContent={<DeployButton />}
          leftDrawerOpen={leftDrawerContentExpanded}
          leftDrawerWidth={leftDrawerWidth}
        />
      </Box>
      <Box
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={{
          border: 0,
          clip: 'rect(0 0 0 0)',
          height: 1,
          margin: -1,
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: 1,
        }}
      >
        {snippetStatus}
      </Box>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={null}
        open={snippetPlacementOpen}
        ref={snippetPlacementSnackbarRef}
        onClose={(_event, reason) => {
          if (reason === 'clickaway') return;
          if (dragDrop.pendingSnippetPlacement) dragDrop.cancelSnippetPlacement();
        }}
        message={snippetPlacementInstructions}
        action={
          dragDrop.pendingSnippetPlacement ? (
            <Button color="primary" size="small" onClick={dragDrop.cancelSnippetPlacement}>
              {t('common.cancel', 'Cancel')}
            </Button>
          ) : undefined
        }
        slotProps={{
          content: {
            role: 'region',
            'aria-label': t('snippets.status.placementRegion', 'Snippet insertion placement'),
            sx: defaultSnackbarContentSx,
          },
        }}
        sx={bottomCenterSnackbarStackSx(snippetPlacementStackIndex)}
      />
      <DragOverlay zIndex={1500} modifiers={[dragDrop.snapLeftToCursor]}>
        <Box width={100} sx={{ opacity: 0.5 }}>
          <img src={dragImage} alt="drag icon" style={{ width: '100px', height: 'auto', pointerEvents: 'none' }} />
        </Box>
      </DragOverlay>

      <PathSelectionDialog
        open={dialogs.pathDialogOpen}
        onClose={dialogs.handlePathDialogClose}
        selectedPathName={dialogs.selectedPathName}
        setSelectedPathName={dialogs.setSelectedPathName}
        paths={dialogs.paths}
        pendingPathDrop={dialogs.pendingPathDrop}
        onConfirm={dialogs.handlePathDialogConfirm}
      />

      <NoPathsDialog open={dialogs.noPathsDialogOpen} onClose={dialogs.handleNoPathsDialogClose} />

      <Dialog open={dialogs.duplicateDriveBaseDialogOpen} onClose={dialogs.handleDuplicateDriveBaseDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>{t('editor.duplicateDriveBaseDialog.title', 'Drive Base already configured')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{formatPlacementInstructions('', 'duplicate-drive-base')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={dialogs.handleDuplicateDriveBaseDialogClose} autoFocus>
            {t('editor.duplicateDriveBaseDialog.ok', 'OK')}
          </Button>
        </DialogActions>
      </Dialog>

      <ActionArgumentsDialog
        open={dialogs.actionArgsDialogOpen}
        onClose={dialogs.handleActionArgsDialogClose}
        pendingDrop={dialogs.pendingActionDrop}
        pathOptions={dialogs.pathOptions}
        onConfirm={dialogs.handleActionArgsDialogConfirm}
        editor={editorInstance}
        driveUnitPortRegistryRev={driveUnitPortRegistryRev}
      />

      <DeviceSnippetPortDialog
        open={dialogs.deviceSnippetPortDialogOpen}
        onClose={dialogs.handleDeviceSnippetPortDialogClose}
        pending={dialogs.pendingDeviceSnippetPortDrop}
        editor={editorInstance}
        driveUnitPortRegistryRev={driveUnitPortRegistryRev}
        onConfirm={dialogs.handleDeviceSnippetPortDialogConfirm}
      />
    </DndContext>
  );
};

export default CodeEditorPage;
